import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import PasswordValidator from 'password-validator';
import PhoneInput from 'react-phone-number-input';
import cn from 'classnames';
import { ReactComponent as VisibleIcon } from 'Assets/eye.svg';
import { ReactComponent as HiddenIcon } from 'Assets/eye.slash.svg';
import 'react-phone-number-input/style.css';
import { AppContext, AuthContext } from 'Context';
import PasswordRequirements from 'Pages/public/authentication/newSign-in/PasswordRequirements';
import { baseUrl } from 'Api';
import { useLocation, useNavigate } from 'react-router-dom';
import ProjectorAcknowlegingTxt from 'Pages/public/authentication/ProjectorAcknowlegingTxt';
import LightRays from 'Components/reactbits/LightRays';
import { AuthPage } from 'Pages/public/authentication/components/page';
import theme from 'Pages/public/authentication/components/auth-theme.module.css';
import inputStyles from 'Pages/public/authentication/components/input-field/index.module.css';
import PaymentPopup from './PaymentPopup';
import styles from './createUser.module.css';

const isDeliverStreamPlan = (plan) => {
  const name = (plan?.product_name || '').trim().toLowerCase();
  return name === 'deliver & stream' || name === 'deliver and stream' || Number(plan?.amount) === 0;
};

// The login screen's backdrop: volumetric light rays fanning down over the
// near-black base, props identical to /newSign-in so both read as one surface.
const RaysBackdrop = () => (
  <div className={theme.rays} aria-hidden="true">
    <LightRays
      raysOrigin="top-center"
      raysColor="#ffffff"
      raysSpeed={0.9}
      lightSpread={0.85}
      rayLength={1.25}
      fadeDistance={1.1}
      saturation={1}
      followMouse
      mouseInfluence={0.08}
      noiseAmount={0.08}
      distortion={0.04}
    />
  </div>
);

function CreateUser() {
  const navigate = useNavigate();
  const location = useLocation();
  const { getToken } = useContext(AuthContext);
  const [showPassword, setShowPassword] = useState(false);
  const [loader, setloader] = useState(false);
  const {
    setapiCALL, handleClick, getSubscription, autoSignIn,
    setAffliatePlanId, setCurrentSelectedPlan,
  } = useContext(AppContext);
  const [emailExists, setEmailExists] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [err, seterr] = useState('');
  const userType = location.state?.userType || 'quick'; // Default to 'quick' if not provided
  const [numberErr, setNumberErr] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [createdUser, setCreatedUser] = useState(null);
  const myToken = getToken();

  // Affiliate plans (with effective_amount) for the plan selection.
  useEffect(() => {
    setPlansLoading(true);
    axios
      .post(`${baseUrl}/getAllActivePlans`, {
        for_affiliate: true,
        token: myToken,
        // Quick-user flow lists a different (quick) plan set.
        ...(userType === 'quick' && { quick_plans: true }),
      })
      .then((res) => setPlans(res.data?.success && Array.isArray(res.data.data) ? res.data.data : []))
      .catch(() => setPlans([]))
      .finally(() => setPlansLoading(false));
  }, []);
  const selectedPlan = plans.find((p) => p.id === selectedPlanId);
  const passwordSchema = new PasswordValidator()
    .is()
    .min(12)
    .has()
    .symbols(1)
    .has()
    .digits(1)
    .has()
    .uppercase(1)
    .lowercase(1);
  const hasUpperCase = /[A-Z]/.test(password);
  const passwordHealth = password
    ? password.length < 12 || !hasUpperCase
      ? 'weak'
      : passwordSchema.validate(password)
        ? password.length >= 12
          ? 'strong'
          : 'medium'
        : 'weak'
    : '';
  const handleEmailChange = ({ target: { value } }) => {
    setEmail(value);
    if (value.includes('@')) {
      setIsLoading(true); // Activate spinner only when '@' is typed
      axios
        .post(`${baseUrl}/signupEmail`, {
          email: value,
        })
        .then((response) => {
          setEmailExists(response.data);
          setIsLoading(false); // Deactivate spinner when response is received
        })
        .catch((error) => {
          console.error(error);
          setIsLoading(false); // Deactivate spinner in case of error
        });
    }
  };

  const validateText = (text) => {
    const textRegex = /^[a-zA-Z\s]*$/;
    return textRegex.test(text);
  };

  const handleFirstNameChange = (event) => {
    const { value } = event.target;
    if (validateText(value)) {
      setFirstName(value);
    }
  };

  const handleLastNameChange = (event) => {
    const { value } = event.target;
    if (validateText(value)) {
      setLastName(value);
    }
  };

  const handlePasswordChange = (event) => {
    const newPassword = event.target.value;
    if (newPassword.length <= 30) {
      setPassword(newPassword);
    }
  };

  const handlePhoneNumberChange = (value) => {
    setPhoneNumber(value || '');
    // Clear error when user starts typing
    if (numberErr) {
      setNumberErr('');
    }
  };

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const validateEmail = (email) => emailRegex.test(email);

  // Switch to the created user's account
  const switchToUserAccount = async (userDetails) => {
    try {
      const formData = new FormData();
      formData.append('token', myToken);
      formData.append('user_id', userDetails.userId || userDetails.id);
      formData.append('user_token', userDetails.token);

      const response = await axios.post(`${baseUrl}/managerUserLogs`, formData);

      if (response?.data?.success === true) {
        handleClick(userDetails);
        localStorage.setItem('firstname', userDetails?.firstname || '');
        localStorage.setItem('lastname', userDetails?.lastname || '');
        // Frontend impersonation markers — the sidebar banner gate reads
        // these on top of the /getUserAccess response, so without them
        // the "Accessed X" banner won't render in the user-creation flow.
        localStorage.setItem('isAccessingUser', 'true');
        localStorage.setItem('accessedUserId', String(userDetails?.userId || userDetails?.id || ''));
        sessionStorage.setItem('quickUserEmail', email);
        navigate('/quick-user-upload', { replace: true });
        getSubscription();
        autoSignIn();
      } else {
        // If switching fails, navigate to plans page as fallback
        navigate('/plans');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error switching to user account:', error);
      // If switching fails, navigate to plans page as fallback
      navigate('/plans');
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prevState) => !prevState);
  };

  const updateProfile = async (phoneNumber, emails, myToken) => {
    const apiUrl = `${baseUrl}/updateMyProfile`;
    const requestBody = {
      mobile: phoneNumber,
      email: emails,
      token: myToken,
    };
    try {
      const response = await axios.post(apiUrl, requestBody);
      return response.data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('API error:', error);
      if (
        error.response
        && error.response.data
        && error.response.data.message
      ) {
        setNumberErr(error.response.data.message);
      } else {
        setNumberErr('An error occurred while updating the profile.');
      }
      throw error;
    }
  };

  const handleSubmit = async () => {
    try {
      // Email validation is required for both quick and withPlan users
      if (!validateEmail(email)) {
        seterr('Please enter a valid email address.');
        setloader(false);
        return;
      }

      // Additional validation for 'withPlan' users
      if (userType === 'withPlan') {
        // Update profile first
        const updateProfileResponse = await updateProfile(phoneNumber, email, myToken);
        if (!updateProfileResponse.success) {
          // eslint-disable-next-line no-console
          console.error('Profile update failed:', updateProfileResponse.message);
          setNumberErr(updateProfileResponse);
          setloader(false);
          return;
        }

        // Validate all required fields
        if (!firstName || !lastName) {
          seterr('First name and last name are required.');
          setloader(false);
          return;
        }

        if (!password || passwordHealth !== 'strong') {
          seterr('Please enter a strong password.');
          setloader(false);
          return;
        }
      }

      // Presence only — same rule as the new signup flow (/newSign-in), which
      // just requires a number and lets PhoneInput handle per-country format
      // and the backend report anything it rejects. A digit-count check here
      // rejected every country whose E.164 length isn't 11-12 digits
      // (e.g. Finland: +358 45 7399 8443 → 13). Required for quick users too,
      // so the quick flow registers a mobile just like the withPlan flow.
      if (!phoneNumber) {
        setNumberErr('Please enter a phone number.');
        setloader(false);
        return;
      }

      if (!selectedPlan) {
        seterr('Please select a plan.');
        setloader(false);
        return;
      }
      const deliverStream = isDeliverStreamPlan(selectedPlan);

      setloader(true);

      // "Deliver & Stream" (free) is granted at registration; every other plan
      // registers without a plan and then collects payment in the popup.
      const payload = {
        token: myToken,
        email,
        mobile: phoneNumber,
        no_plan: !deliverStream,
        ...(deliverStream && { plan_id: selectedPlanId }),
      };
      if (userType === 'withPlan') {
        payload.password = password;
        payload.firstname = firstName;
        payload.lastname = lastName;
      }

      axios
        .post(`${baseUrl}/userRegistration`, payload)
        .then(async (response) => {
          setapiCALL(response.data);
          const created = response.data?.data?.userDetails?.[0];

          if (deliverStream) {
            if (response.data?.subscription === true && created) await switchToUserAccount(created);
            else navigate('/affiliate');
            setloader(false);
            return;
          }

          // Paid → open the payment popup. Prime what the payment payload needs.
          const createdUserId = created?.userId || created?.id;
          if (createdUserId) localStorage.setItem('affliate_userId', String(createdUserId));
          setAffliatePlanId?.(selectedPlanId);
          setCurrentSelectedPlan?.(selectedPlan);
          localStorage.setItem('planId', String(selectedPlanId));
          setCreatedUser(created);
          setPayOpen(true);
          setloader(false);
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('User registration error:', error);
          seterr(error?.response?.data?.message || 'Something went wrong. Please try again.');
          setloader(false);
        });
    } catch (error) {
      // Handle errors from the updateProfile function
      // eslint-disable-next-line no-console
      console.error(error);
      setloader(false);
    }
  };

  // Unchanged from the old markup: which of the two states the submit button is
  // in, and whether clicking it does anything.
  const isSubmittable = emailRegex.test(email)
    && !!selectedPlanId
    && !!phoneNumber
    && (userType === 'quick'
      || (userType === 'withPlan' && firstName && lastName && password && passwordHealth === 'strong'))
    && !loader;
  const isDisabled = !emailRegex.test(email)
    || emailExists?.message === 'Email already exists'
    || !selectedPlanId
    || !phoneNumber
    || (userType === 'withPlan' && (!firstName || !lastName || !password || passwordHealth !== 'strong'))
    || loader;

  const renderPlan = (plan) => {
    const aff = plan.affiliate;
    const full = Number(plan.amount);
    const eff = aff ? Number(aff.effective_amount) : full;
    const isFree = eff === 0;
    const discounted = eff < full;
    const selected = selectedPlanId === plan.id;
    let off = '';
    if (aff?.discount && !isFree) {
      const d = aff.discount;
      if (d.type === 'percentage' && d.value) off = `${d.value}% off`;
      else if (d.value) off = `$${(Number(d.value) / 100).toFixed(2)} off`;
    }
    return (
      <div
        key={plan.id}
        role="button"
        tabIndex={0}
        onKeyDown={() => {}}
        onClick={() => setSelectedPlanId(plan.id)}
        className={cn(styles.planCard, { [styles.planCardSelected]: selected })}
      >
        {(isFree || off) && (
          <span className={cn(styles.planCardBadge, isFree ? styles.freeBadge : styles.offBadge)}>
            {isFree ? 'FREE' : off}
          </span>
        )}
        <span className={styles.planName}>{plan.product_name}</span>
        <div className={styles.priceRow}>
          <span className={styles.eff}>{`$${eff.toFixed(2)}`}</span>
          {discounted && !isFree && <span className={styles.full}>{`$${full.toFixed(2)}`}</span>}
        </div>
        <span className={styles.planSub}>{plan.price_description || ' '}</span>
      </div>
    );
  };

  return (
    <AuthPage className={theme.page} contentClassName={theme.content}>
      <RaysBackdrop />
      <button
        type="button"
        className={theme.back}
        onClick={() => navigate('/settings/manage-users')}
      >
        Back
      </button>

      <div className={cn(theme.card, theme.stepIn, styles.card)}>
        <div className={theme.head}>
          <h1 className={theme.title}>Create account for new user</h1>
          <p className={theme.subtitle}>Enter the information of the new user</p>
        </div>

        <div className={cn(styles.field, styles.fieldRelative)}>
          <input
            placeholder="Email"
            className={styles.input}
            type="email"
            value={email}
            onChange={handleEmailChange}
            autoFocus
          />
          {email.includes('@')
            && email.includes('.')
            && email.split('.').pop().length >= 2
            && isLoading && (
              <span className={styles.spinnerSlot}>
                <div
                  className={cn({
                    [inputStyles.spinnerContainer]: true,
                    [inputStyles.loading]: true,
                  })}
                >
                  <div
                    style={{ borderColor: '#ffffff44' }}
                    className={inputStyles.spinner}
                  />
                  <div
                    style={{ borderColor: '#5aa5ef' }}
                    className={cn({
                      [inputStyles.spinner]: true,
                      [inputStyles.animatedSpinner]: true,
                    })}
                  />
                </div>
              </span>
          )}
        </div>
        {emailExists?.message === 'Email already exists' && (
          <span className={styles.error}>{emailExists?.message || err}</span>
        )}
        {err && <span className={styles.error}>{err}</span>}

        {/* Additional fields for 'withPlan' user type */}
        {userType === 'withPlan' && (
          <div className={styles.nameRow}>
            <div className={styles.field}>
              <input
                placeholder="First Name"
                className={styles.input}
                type="text"
                value={firstName}
                onChange={handleFirstNameChange}
              />
            </div>
            <div className={styles.field}>
              <input
                placeholder="Last Name"
                className={styles.input}
                type="text"
                value={lastName}
                onChange={handleLastNameChange}
              />
            </div>
          </div>
        )}

        {/* Phone number is collected for both flows — the quick user is
            registered with a mobile just like the withPlan user. */}
        <div className={styles.phoneField}>
          <PhoneInput
            placeholder="Phone Number"
            value={phoneNumber}
            onChange={handlePhoneNumberChange}
            countryCallingCodeEditable={false}
            defaultCountry="US"
          />
        </div>
        {numberErr && <span className={styles.error}>{numberErr}</span>}

        {userType === 'withPlan' && (
          <div className={cn(styles.field, styles.passwordField)}>
            <input
              placeholder="Password"
              className={styles.input}
              type={showPassword ? 'text' : 'password'}
              minLength={12}
              maxLength={30}
              autoComplete="new-password"
              value={password}
              onChange={handlePasswordChange}
            />
            <button
              type="button"
              className={styles.eyeToggle}
              onClick={handleTogglePasswordVisibility}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              title={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? (
                <HiddenIcon className={inputStyles.passwordVisibilityToggleIcon} />
              ) : (
                <VisibleIcon className={inputStyles.passwordVisibilityToggleIcon} />
              )}
            </button>
          </div>
        )}

        <p className={styles.planLabel}>Select a plan</p>
        {plansLoading ? (
          <p className={styles.planState}>Loading plans…</p>
        ) : !plans.length ? (
          <p className={styles.planState}>No plans available.</p>
        ) : (
          <div className={styles.planCards}>{plans.map(renderPlan)}</div>
        )}

        <div className={styles.acknowledge}>
          {userType === 'withPlan' && password ? (
            !password || passwordHealth === 'strong' ? (
              <ProjectorAcknowlegingTxt />
            ) : (
              <PasswordRequirements password={password} />
            )
          ) : (
            <ProjectorAcknowlegingTxt />
          )}
        </div>

        <button
          type="button"
          onClick={isSubmittable ? handleSubmit : null}
          disabled={isDisabled}
          className={theme.primary}
        >
          {loader === true ? (
            <svg className={cn('loading-circle', styles.buttonSpinner)} viewBox="0 0 50 50">
              <circle
                className="circle"
                cx="25"
                cy="25"
                r="20"
                fill="none"
                strokeWidth="4"
              />
            </svg>
          ) : (
            'Agree and Continue'
          )}
        </button>
      </div>

      <PaymentPopup
        open={payOpen}
        onClose={() => setPayOpen(false)}
        plan={selectedPlan}
        user={createdUser}
        onPaid={() => switchToUserAccount(createdUser)}
      />
    </AuthPage>
  );
}

export default CreateUser;
